// @ts-check
/* eslint-env browser */

import * as turf from '@turf/turf'
import { getVectorLayerIds } from './vector-tile-utils.js'

/**
 * Handles snapping to OS vector tile features (vertices/endpoints only)
 */
export class Snapping {
  #SNAP_DISTANCE = 15 // pixels
  #MAX_FEATURES = 20
  #CLOSE_ENOUGH_THRESHOLD = 0.5
  #COORDINATE_PRECISION = 6

  #map
  #handler
  #isEnabled = false

  /**
   * @param {import('maplibre-gl').Map} map
   */
  constructor (map) {
    this.#map = map
    this.#handler = (event) => {
      try {
        if (!event.lngLat || !event.point) {
          return
        }

        const snapPoint = this.#findSnapPoint(
          event.lngLat.lng,
          event.lngLat.lat
        )

        if (snapPoint) {
          event.lngLat.lng = snapPoint[0]
          event.lngLat.lat = snapPoint[1]
          if (event.lngLat.wrap) {
            event.lngLat.wrap()
          }
        }
      } catch (error) {
        console.debug('Vector tile snapping error:', error)
      }
    }
  }

  /**
   * Queries vector tile features near a point and finds the closest snap point
   * Only snaps to vertices (endpoints), not arbitrary points along lines
   * @param {number} lng
   * @param {number} lat
   * @returns {[number, number] | null}
   */
  #findSnapPoint (lng, lat) {
    const vectorLayers = getVectorLayerIds(this.#map)
    if (vectorLayers.length === 0) {
      return null
    }

    const radius = this.#SNAP_DISTANCE
    const point = this.#map.project([lng, lat])
    const bbox = [
      [point.x - radius, point.y - radius],
      [point.x + radius, point.y + radius]
    ]

    const features = this.#map.queryRenderedFeatures(bbox, {
      layers: vectorLayers
    })

    if (features.length === 0) {
      return null
    }

    const featuresToProcess = features.slice(0, this.#MAX_FEATURES)

    const uniqueVertices = new Map()

    for (const feature of featuresToProcess) {
      const geometry = feature.geometry
      if (!geometry) continue

      const coords = turf.coordAll(turf.feature(geometry))

      for (const coord of coords) {
        const key = `${coord[0].toFixed(this.#COORDINATE_PRECISION)},${coord[1].toFixed(this.#COORDINATE_PRECISION)}`
        uniqueVertices.set(key, [coord[0], coord[1]])
      }
    }

    let closestSnapPoint = null
    let minScreenDistance = Infinity
    const closeEnoughThreshold = radius * this.#CLOSE_ENOUGH_THRESHOLD

    for (const [, coord] of uniqueVertices) {
      const proj = this.#map.project(coord)
      const dist = Math.sqrt((point.x - proj.x) ** 2 + (point.y - proj.y) ** 2)
      if (dist < minScreenDistance && dist <= radius) {
        minScreenDistance = dist
        closestSnapPoint = coord
        if (dist <= closeEnoughThreshold) {
          break
        }
      }
    }

    return closestSnapPoint
  }

  /**
   * Enables snapping to OS vector tile features
   */
  enable () {
    if (this.#isEnabled) return
    this.#isEnabled = true
    this.#map.on('mousemove', this.#handler)
    this.#map.on('touchmove', this.#handler)
  }

  /**
   * Disables snapping to OS vector tile features
   */
  disable () {
    if (!this.#isEnabled) return
    this.#isEnabled = false
    this.#map.off('mousemove', this.#handler)
    this.#map.off('touchmove', this.#handler)
  }
}
